// ─────────────────────────────────────────────────────────────────────────────
// Campaign Studio — type-driven campaign generation for the MTN Social Hub.
// Instead of generic weekly content, every campaign is built around a focus:
// a Product, the Brand, a Sale, a Theme, or a Promotion. The AI follows the
// creative direction of the chosen type. A 5-step wizard with full forward /
// backward navigation keeps every existing control (edit, regenerate, image
// generation, approve, reject, delete) available.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import {
  Sparkles, Check, X, Pencil, RefreshCw, Image as ImageIcon, Trash2, ChevronDown,
  ChevronLeft, ChevronRight, Instagram, Package, Megaphone, Tag, Layers, BadgePercent,
  Rocket, ClipboardCheck, Send, Target,
} from 'lucide-react';

import { supabase } from '@/lib/supabase';
import {
  CAMPAIGN_TYPES, campaignTypeOf, CampaignTypeDef,
  MtnWeek, MtnPost, MtnProduct, pj, fetchPosts, logEvent,
  deletePostsByIds, deleteWeekCascade,
} from '@/lib/mtnshop';
import { Glass, SectionTitle, GButton, Pill, Field, inputCls, Spinner } from '@/components/mtnshop/MtnUi';
import InstagramPostCard from '@/components/mtnshop/InstagramPostCard';
import StoryPhoneFrame from '@/components/mtnshop/StoryPhoneFrame';

const STEPS = [
  { label: 'Campaign Focus', desc: 'Pick the type & focus' },
  { label: 'Generate', desc: 'AI builds the campaign' },
  { label: 'Review & Edit', desc: 'Previews & full editing' },
  { label: 'Approve', desc: 'Approval console' },
  { label: 'Publish', desc: 'Finalize & export' },
];

const TYPE_ICONS: Record<string, React.ReactNode> = {
  product: <Package className="h-5 w-5" />,
  brand: <Megaphone className="h-5 w-5" />,
  sale: <BadgePercent className="h-5 w-5" />,
  theme: <Layers className="h-5 w-5" />,
  promotion: <Tag className="h-5 w-5" />,
};

const TITLE_PREFIX: Record<string, string> = {
  product: 'Product Spotlight',
  brand: 'Brand Story',
  sale: 'Sale',
  theme: '',
  promotion: 'Promo',
};

function campaignTitle(type: CampaignTypeDef, focus: string): string {
  const prefix = TITLE_PREFIX[type.id];
  return prefix ? `${prefix}: ${focus}` : focus;
}

export default function CampaignStudio({ weeks, products, refresh }: {
  weeks: MtnWeek[]; products: MtnProduct[]; refresh: () => void;
}) {
  // ---- Wizard step state (forward & backward navigation) ----
  const [step, setStep] = useState(weeks.length > 0 ? 2 : 0);

  // ---- Campaign setup ----
  const [typeId, setTypeId] = useState<string>(CAMPAIGN_TYPES[0].id);
  const [focus, setFocus] = useState('');
  const [days, setDays] = useState(7);
  const [notes, setNotes] = useState('');
  const typeDef = campaignTypeOf(typeId);

  // ---- Generation ----
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // ---- Selected campaign + posts ----
  const [weekId, setWeekId] = useState<string | null>(weeks[0]?.id || null);
  const [posts, setPosts] = useState<MtnPost[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  // ---- Edit modal ----
  const [editing, setEditing] = useState<MtnPost | null>(null);
  const [eCaption, setECaption] = useState(''); const [eHashtags, setEHashtags] = useState(''); const [ePrompt, setEPrompt] = useState('');

  // ---- Delete-by-day dropdown ----
  const [delOpen, setDelOpen] = useState(false);
  const [delSel, setDelSel] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);

  // ---- Publish ----
  const [publishing, setPublishing] = useState(false);

  const week = weeks.find(w => w.id === weekId) || null;
  const weekType = campaignTypeOf(week?.campaign_type);

  useEffect(() => { if (weekId) fetchPosts(weekId).then(setPosts); else setPosts([]); }, [weekId]);
  useEffect(() => { if (!weekId && weeks[0]) setWeekId(weeks[0].id); }, [weeks, weekId]);
  useEffect(() => { setDelSel(new Set()); setDelOpen(false); }, [weekId]);

  const reloadPosts = async () => { if (weekId) setPosts(await fetchPosts(weekId)); };

  // Campaigns organized by type for the library
  const grouped = useMemo(() => CAMPAIGN_TYPES
    .map(t => ({ type: t, items: weeks.filter(w => (w.campaign_type || 'theme') === t.id) }))
    .filter(g => g.items.length > 0), [weeks]);

  // ---- Step navigation guards ----
  const canNextFrom = (s: number): boolean => {
    if (s === 0) return focus.trim().length > 0;
    if (s === 1) return !!weekId && posts.length > 0;
    return true;
  };
  const goto = (s: number) => setStep(Math.max(0, Math.min(STEPS.length - 1, s)));

  // ---- Actions (all preserved from the previous studio) ----
  const generate = async () => {
    setGenerating(true); setError('');
    const { data, error: err } = await supabase.functions.invoke('mtn-generate-week', {
      body: { campaignType: typeId, focus: focus.trim(), title: campaignTitle(typeDef, focus.trim()), notes, days },
    });
    setGenerating(false);
    if (err || data?.error) { setError(data?.error || err?.message || 'Generation failed'); return; }
    await refresh();
    if (data?.week?.id) { setWeekId(data.week.id); setStep(2); }
  };

  const setStatus = async (p: MtnPost, status: string) => {
    let revision_notes: string | null = p.revision_notes;
    if (status === 'rejected') revision_notes = window.prompt('Revision notes (the AI learns from these):', '') || null;
    await supabase.from('mtn_shop_posts').update({ status, revision_notes }).eq('id', p.id);
    await logEvent(status === 'approved' ? 'approve' : 'reject', `${p.day_name} ${p.post_type} ${status}${revision_notes ? ': ' + revision_notes : ''}`);
    reloadPosts();
  };

  const regen = async (p: MtnPost) => {
    const n = window.prompt('Optional notes for the regeneration:', '') || '';
    setBusy(p.id);
    await supabase.functions.invoke('mtn-regenerate-post', { body: { postId: p.id, notes: n } });
    setBusy(null); reloadPosts();
  };

  // Map postId → real catalog products the image pipeline matched & referenced
  const [imgMatches, setImgMatches] = useState<Record<string, string[]>>({});

  const genImage = async (p: MtnPost) => {
    setBusy(p.id);
    // The edge function now matches the EXACT catalog products for this post
    // (product_refs + campaign focus) server-side and uses their real scraped
    // photos as visual reference — no client-side guessing needed.
    const { data } = await supabase.functions.invoke('mtn-generate-post-image', { body: { postId: p.id } });
    if (data?.matchedProducts) setImgMatches(m => ({ ...m, [p.id]: data.matchedProducts }));
    setBusy(null); reloadPosts();
  };


  const saveEdit = async () => {
    if (!editing) return;
    await supabase.from('mtn_shop_posts').update({ caption: eCaption, hashtags: eHashtags, image_prompt: ePrompt }).eq('id', editing.id);
    await logEvent('edit', `Admin edited ${editing.day_name} ${editing.post_type} copy.`);
    setEditing(null); reloadPosts();
  };

  const delOne = async (p: MtnPost) => {
    if (!window.confirm(`Permanently delete the ${p.day_name || 'day'} ${p.post_type === 'feed' ? 'feed post' : 'story'}? This cannot be undone.`)) return;
    setBusy(p.id);
    await deletePostsByIds([p.id]);
    await logEvent('delete', `Admin deleted ${p.day_name} ${p.post_type}${week ? ` from "${week.theme}"` : ''}.`);
    setBusy(null);
    setDelSel(s => { const n = new Set(s); n.delete(p.id); return n; });
    await reloadPosts(); refresh();
  };

  const toggleSel = (ids: string[], on: boolean) => {
    setDelSel(prev => {
      const next = new Set(prev);
      ids.forEach(id => { if (on) next.add(id); else next.delete(id); });
      return next;
    });
  };

  const deleteSelected = async () => {
    if (delSel.size === 0) return;
    if (!window.confirm(`Permanently delete ${delSel.size} selected item${delSel.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    setDeleting(true);
    await deletePostsByIds(Array.from(delSel));
    await logEvent('delete', `Admin deleted ${delSel.size} item(s) via day selection${week ? ` from "${week.theme}"` : ''}.`);
    setDeleting(false); setDelSel(new Set()); setDelOpen(false);
    await reloadPosts(); refresh();
  };

  const deleteWholeCampaign = async () => {
    if (!week) return;
    if (!window.confirm(`Delete the ENTIRE "${week.theme}" campaign — all posts and stories? This cannot be undone.`)) return;
    setDeleting(true);
    await deleteWeekCascade(week.id);
    await logEvent('delete', `Admin deleted the entire "${week.theme}" campaign.`);
    setDeleting(false); setDelOpen(false); setDelSel(new Set()); setWeekId(null);
    await refresh();
  };

  const approveAllRemaining = async () => {
    const remaining = posts.filter(p => p.status !== 'approved');
    if (remaining.length === 0) return;
    if (!window.confirm(`Approve all ${remaining.length} remaining item${remaining.length > 1 ? 's' : ''}?`)) return;
    await supabase.from('mtn_shop_posts').update({ status: 'approved' }).in('id', remaining.map(p => p.id));
    await logEvent('approve', `Admin bulk-approved ${remaining.length} item(s) in "${week?.theme}".`);
    reloadPosts();
  };

  const markReady = async () => {
    if (!week) return;
    setPublishing(true);
    await supabase.from('mtn_shop_weeks').update({ status: 'ready' }).eq('id', week.id);
    await logEvent('publish', `Campaign "${week.theme}" marked ready to publish.`);
    setPublishing(false); refresh();
  };

  const feeds = posts.filter(p => p.post_type === 'feed');
  const stories = posts.filter(p => p.post_type === 'story');
  const approvedCount = posts.filter(p => p.status === 'approved').length;
  const rejectedCount = posts.filter(p => p.status === 'rejected').length;
  const withImages = posts.filter(p => p.image_url).length;

  const dayGroups = useMemo(() => {
    const map = new Map<number, { dayIndex: number; dayName: string; items: MtnPost[] }>();
    posts.forEach(p => {
      const g = map.get(p.day_index) || { dayIndex: p.day_index, dayName: p.day_name || `Day ${p.day_index + 1}`, items: [] };
      g.items.push(p); map.set(p.day_index, g);
    });
    return Array.from(map.values()).sort((a, b) => a.dayIndex - b.dayIndex);
  }, [posts]);

  // Shared per-card action row (feed size)
  const feedActions = (p: MtnPost) => (
    <div className="flex flex-wrap gap-1.5">
      <GButton variant="success" className="px-2.5 py-1.5 text-xs" onClick={() => setStatus(p, 'approved')}><Check className="h-3.5 w-3.5" /> Approve</GButton>
      <GButton variant="danger" className="px-2.5 py-1.5 text-xs" onClick={() => setStatus(p, 'rejected')}><X className="h-3.5 w-3.5" /> Reject</GButton>
      <GButton variant="ghost" className="px-2.5 py-1.5 text-xs" onClick={() => { setEditing(p); setECaption(p.caption || ''); setEHashtags(p.hashtags || ''); setEPrompt(p.image_prompt || ''); }}><Pencil className="h-3.5 w-3.5" /> Edit</GButton>
      <GButton variant="ghost" className="px-2.5 py-1.5 text-xs" onClick={() => regen(p)} disabled={busy === p.id}>{busy === p.id ? <Spinner /> : <RefreshCw className="h-3.5 w-3.5" />} Regenerate</GButton>
      <GButton variant="ghost" className="px-2.5 py-1.5 text-xs" onClick={() => genImage(p)} disabled={busy === p.id}>{busy === p.id ? <Spinner /> : <ImageIcon className="h-3.5 w-3.5" />} {p.image_url ? 'New image' : 'Generate image'}</GButton>
      <GButton variant="ghost" className="px-2.5 py-1.5 text-xs text-red-300 hover:text-red-200" onClick={() => delOne(p)} disabled={busy === p.id} title="Delete permanently">
        {busy === p.id ? <Spinner /> : <Trash2 className="h-3.5 w-3.5" />} Delete
      </GButton>
    </div>
  );

  // Shows which EXACT catalog products anchor this post's visuals — either the
  // products the image pipeline just matched (real photos used as reference),
  // or the post's product_refs the AI committed to at generation time.
  const productAnchor = (p: MtnPost) => {
    const matched = imgMatches[p.id];
    const refs = pj<string[]>(p.product_refs, []);
    const names = matched && matched.length > 0 ? matched : refs;
    if (!names || names.length === 0) return null;
    return (
      <p className="flex items-start gap-1.5 text-[11px] leading-snug text-zinc-500">
        <Package className={`mt-0.5 h-3 w-3 shrink-0 ${matched && matched.length > 0 ? 'text-emerald-400' : 'text-zinc-500'}`} />
        <span className="line-clamp-2">
          {matched && matched.length > 0 ? 'Image anchored to real product photos: ' : 'Featured products: '}
          <span className="text-zinc-400">{names.join(' · ')}</span>
        </span>
      </p>
    );
  };


  return (
    <div className="space-y-8">
      <SectionTitle kicker="Campaign-Driven Workflow" title="Campaign Studio"
        desc="Build focused campaigns around a product, the brand, a sale, a theme or a promotion. The AI Brain follows the creative direction of the campaign type and connects every post into one story." />

      {/* ── Stepper: clickable pills + forward/backward controls ── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {STEPS.map((s, i) => {
            const enabled = i <= step || (i === step + 1 && canNextFrom(step)) || (i >= 2 && weekId && posts.length > 0);
            return (
              <React.Fragment key={s.label}>
                <button
                  onClick={() => enabled && goto(i)}
                  disabled={!enabled}
                  className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium border whitespace-nowrap transition
                    ${i === step ? 'bg-white text-black border-white'
                      : i < step ? 'bg-sky-500/15 text-sky-300 border-sky-500/25 hover:bg-sky-500/25'
                      : enabled ? 'bg-white/[0.05] text-zinc-300 border-white/10 hover:bg-white/[0.1]'
                      : 'bg-white/[0.03] text-zinc-600 border-white/[0.06] cursor-not-allowed'}`}>
                  <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${i === step ? 'bg-black text-white' : i < step ? 'bg-sky-400 text-black' : 'bg-white/10 text-zinc-400'}`}>
                    {i < step ? <Check className="h-2.5 w-2.5" /> : i + 1}
                  </span>
                  {s.label}
                </button>
                {i < STEPS.length - 1 && <div className="h-px w-4 bg-white/10 shrink-0" />}
              </React.Fragment>
            );
          })}
        </div>
        <div className="flex items-center justify-between">
          <GButton variant="ghost" className="px-3 py-1.5 text-xs" onClick={() => goto(step - 1)} disabled={step === 0}>
            <ChevronLeft className="h-3.5 w-3.5" /> Back{step > 0 ? `: ${STEPS[step - 1].label}` : ''}
          </GButton>
          <p className="text-[11px] text-zinc-500 hidden sm:block">{STEPS[step].desc}</p>
          <GButton variant="ghost" className="px-3 py-1.5 text-xs" onClick={() => goto(step + 1)} disabled={step === STEPS.length - 1 || !canNextFrom(step)}>
            Next{step < STEPS.length - 1 ? `: ${STEPS[step + 1].label}` : ''} <ChevronRight className="h-3.5 w-3.5" />
          </GButton>
        </div>
      </div>

      {/* ═════════ STEP 1 — CAMPAIGN FOCUS ═════════ */}
      {step === 0 && (
        <div className="space-y-6">
          <div>
            <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2"><Target className="h-4 w-4 text-sky-400" /> What is this campaign built around?</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {CAMPAIGN_TYPES.map(t => (
                <button key={t.id} onClick={() => { setTypeId(t.id); setFocus(''); }}
                  className={`rounded-2xl border p-4 text-left transition ${typeId === t.id ? 'border-sky-400/60 bg-sky-500/10' : 'border-white/10 bg-white/[0.04] hover:bg-white/[0.08]'}`}>
                  <div className={`mb-2 inline-flex rounded-xl p-2 ${typeId === t.id ? 'bg-sky-400 text-black' : 'bg-white/[0.07] text-zinc-300'}`}>{TYPE_ICONS[t.id]}</div>
                  <p className="text-sm font-semibold text-white">{t.label} Campaign</p>
                  <p className="mt-1 text-[11px] leading-snug text-zinc-500">{t.tagline}</p>
                </button>
              ))}
            </div>
          </div>

          <Glass className="p-6 space-y-5">
            <Field label={typeDef.focusLabel}>
              <input className={inputCls} value={focus} onChange={e => setFocus(e.target.value)} placeholder={typeDef.focusPlaceholder} />
            </Field>
            <div>
              <p className="text-xs font-medium text-zinc-400 mb-2">Quick picks</p>
              <div className="flex flex-wrap gap-2">
                {typeDef.suggestions.map(s => (
                  <button key={s} onClick={() => setFocus(s)}
                    className={`rounded-full border px-3 py-1.5 text-xs transition ${focus === s ? 'bg-white text-black border-white' : 'bg-white/[0.05] text-zinc-300 border-white/10 hover:bg-white/[0.1]'}`}>
                    {s}
                  </button>
                ))}
                {typeDef.id === 'product' && products.filter(p => p.favorite).slice(0, 5).map(p => (
                  <button key={p.id} onClick={() => setFocus(p.name)}
                    className={`rounded-full border px-3 py-1.5 text-xs transition flex items-center gap-1.5 ${focus === p.name ? 'bg-white text-black border-white' : 'bg-amber-500/10 text-amber-300 border-amber-500/20 hover:bg-amber-500/20'}`}>
                    ★ {p.name.slice(0, 40)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Campaign length">
                <select className={inputCls} value={days} onChange={e => setDays(Number(e.target.value))}>
                  <option value={3} className="bg-zinc-900">3 posts + 3 stories (short burst)</option>
                  <option value={5} className="bg-zinc-900">5 posts + 5 stories (work week)</option>
                  <option value={7} className="bg-zinc-900">7 posts + 7 stories (full week)</option>
                </select>
              </Field>
              <Field label="Direction for the AI (optional)">
                <input className={inputCls} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. promo ends Friday, emphasize free shipping" />
              </Field>
            </div>
            <div className="flex justify-end">
              <GButton onClick={() => goto(1)} disabled={!canNextFrom(0)}>
                Continue to Generate <ChevronRight className="h-4 w-4" />
              </GButton>
            </div>
          </Glass>
        </div>
      )}

      {/* ═════════ STEP 2 — GENERATE ═════════ */}
      {step === 1 && (
        <Glass className="p-6 space-y-5">
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-sky-500/15 p-3 text-sky-300">{TYPE_ICONS[typeDef.id]}</div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-lg font-semibold text-white">{campaignTitle(typeDef, focus || '…')}</p>
                <Pill tone={typeDef.tone}>{typeDef.label} campaign</Pill>
              </div>
              <p className="mt-1 text-sm text-zinc-400">{typeDef.tagline}</p>
              <div className="mt-3 grid sm:grid-cols-3 gap-3 text-xs">
                <div className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-3"><p className="text-zinc-500">Focus</p><p className="mt-0.5 text-white font-medium">{focus || '—'}</p></div>
                <div className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-3"><p className="text-zinc-500">Length</p><p className="mt-0.5 text-white font-medium">{days} feed posts + {days} stories</p></div>
                <div className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-3"><p className="text-zinc-500">Direction notes</p><p className="mt-0.5 text-white font-medium">{notes || 'None'}</p></div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 border-t border-white/[0.07] pt-4">
            <GButton onClick={generate} disabled={generating || !focus.trim()}>
              {generating ? <Spinner /> : <Sparkles className="h-4 w-4" />} {generating ? 'AI Brain building the campaign…' : 'Generate Campaign'}
            </GButton>
            <GButton variant="ghost" onClick={() => goto(0)}><ChevronLeft className="h-4 w-4" /> Change focus</GButton>
            {weekId && posts.length > 0 && (
              <GButton variant="ghost" onClick={() => goto(2)}>Skip — review existing campaigns <ChevronRight className="h-4 w-4" /></GButton>
            )}
          </div>
          {generating && <p className="text-xs text-sky-300 flex items-center gap-2"><Spinner /> Building the {typeDef.label.toLowerCase()} narrative arc, {days} feed posts and {days} stories from the knowledge base — this takes ~30-60 seconds.</p>}
          {error && <p className="text-xs text-red-400">{error}</p>}
        </Glass>
      )}

      {/* ═════════ STEP 3 — REVIEW & EDIT ═════════ */}
      {step === 2 && (
        <div className="space-y-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Campaign library — organized by campaign type */}
            <Glass className="p-4 lg:w-80 shrink-0">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Campaign Library</p>
                <GButton variant="ghost" className="px-2.5 py-1 text-[11px]" onClick={() => goto(0)}><Sparkles className="h-3 w-3" /> New</GButton>
              </div>
              <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
                {grouped.map(g => (
                  <div key={g.type.id}>
                    <div className="mb-1.5 flex items-center gap-2">
                      <span className="text-zinc-500">{React.cloneElement(TYPE_ICONS[g.type.id] as React.ReactElement, { className: 'h-3.5 w-3.5' })}</span>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{g.type.label} campaigns</p>
                      <span className="ml-auto text-[10px] text-zinc-600">{g.items.length}</span>
                    </div>
                    <div className="space-y-1.5">
                      {g.items.map(w => (
                        <button key={w.id} onClick={() => setWeekId(w.id)}
                          className={`w-full rounded-xl px-3 py-2 text-left text-sm transition ${w.id === weekId ? 'bg-white text-black font-medium' : 'text-zinc-300 hover:bg-white/[0.07]'}`}>
                          <p className="truncate">{w.theme}</p>
                          <p className={`text-[10px] ${w.id === weekId ? 'text-zinc-600' : 'text-zinc-600'}`}>
                            {w.focus && w.focus !== w.theme ? `${w.focus} · ` : ''}{new Date(w.created_at).toLocaleDateString()}{w.status === 'ready' ? ' · ready' : ''}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                {weeks.length === 0 && <p className="text-xs text-zinc-500">No campaigns yet — go back to Campaign Focus to create the first one.</p>}
              </div>
            </Glass>

            {/* Selected campaign header + strategy + delete controls */}
            {week ? (
              <Glass className="p-5 flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Pill tone={weekType.tone}>{weekType.label} campaign</Pill>
                  <p className="text-white font-semibold">{week.theme}</p>
                  <Pill tone={approvedCount === posts.length && posts.length > 0 ? 'emerald' : 'amber'}>
                    {approvedCount}/{posts.length} approved
                  </Pill>
                  {week.status === 'ready' && <Pill tone="emerald"><Send className="h-3 w-3 mr-1" /> ready</Pill>}

                  {/* Delete-by-day dropdown */}
                  <div className="relative ml-auto">
                    <GButton variant="danger" className="px-3 py-1.5 text-xs" onClick={() => setDelOpen(o => !o)}>
                      <Trash2 className="h-3.5 w-3.5" /> Delete content
                      {delSel.size > 0 && <span className="rounded-full bg-white/20 px-1.5 text-[10px]">{delSel.size}</span>}
                      <ChevronDown className={`h-3.5 w-3.5 transition ${delOpen ? 'rotate-180' : ''}`} />
                    </GButton>
                    {delOpen && (
                      <div className="absolute right-0 z-40 mt-2 w-80 rounded-2xl border border-white/10 bg-zinc-950/95 p-4 shadow-2xl backdrop-blur-xl">
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Select days to delete</p>
                          <button onClick={() => setDelOpen(false)} className="text-zinc-500 hover:text-white"><X className="h-4 w-4" /></button>
                        </div>
                        {dayGroups.length === 0 && <p className="text-xs text-zinc-500">No posts or stories in this campaign.</p>}
                        <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                          {dayGroups.map(g => {
                            const dayIds = g.items.map(i => i.id);
                            const allOn = dayIds.every(id => delSel.has(id));
                            return (
                              <div key={g.dayIndex} className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-2.5">
                                <label className="flex cursor-pointer items-center gap-2">
                                  <input type="checkbox" checked={allOn} onChange={e => toggleSel(dayIds, e.target.checked)} className="h-3.5 w-3.5 accent-red-500" />
                                  <span className="text-sm font-medium text-white">{g.dayName}</span>
                                  <span className="ml-auto text-[10px] text-zinc-500">{g.items.length} item{g.items.length > 1 ? 's' : ''}</span>
                                </label>
                                <div className="mt-1.5 space-y-1" style={{ paddingLeft: '1.4rem' }}>
                                  {g.items.map(i => (
                                    <label key={i.id} className="flex cursor-pointer items-center gap-2 text-xs text-zinc-400 hover:text-zinc-200">
                                      <input type="checkbox" checked={delSel.has(i.id)} onChange={e => toggleSel([i.id], e.target.checked)} className="h-3 w-3 accent-red-500" />
                                      {i.post_type === 'feed' ? <ImageIcon className="h-3 w-3 text-sky-400" /> : <Instagram className="h-3 w-3 text-fuchsia-400" />}
                                      <span className="truncate">{i.post_type === 'feed' ? `Feed post${i.title ? ` — ${i.title}` : ''}` : 'Story'}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {dayGroups.length > 0 && (
                          <div className="mt-3 flex items-center gap-2 border-t border-white/[0.07] pt-3">
                            <button onClick={() => toggleSel(posts.map(p => p.id), delSel.size !== posts.length)} className="text-[11px] text-zinc-400 hover:text-white underline underline-offset-2">
                              {delSel.size === posts.length ? 'Clear all' : 'Select all'}
                            </button>
                            <GButton variant="danger" className="ml-auto px-3 py-1.5 text-xs" onClick={deleteSelected} disabled={deleting || delSel.size === 0}>
                              {deleting ? <Spinner /> : <Trash2 className="h-3.5 w-3.5" />} Delete selected ({delSel.size})
                            </GButton>
                          </div>
                        )}
                        <button onClick={deleteWholeCampaign} disabled={deleting}
                          className="mt-2 w-full rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-300 transition hover:bg-red-500/20">
                          Delete entire campaign
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {week.focus && <p className="text-xs text-zinc-500 mb-1.5">Focus: <span className="text-zinc-300">{week.focus}</span></p>}
                <p className="text-sm text-zinc-400 leading-relaxed">{week.strategy}</p>
              </Glass>
            ) : (
              <Glass className="p-10 flex-1 text-center">
                <p className="text-sm text-zinc-400">Select a campaign from the library, or create a new one from the Campaign Focus step.</p>
              </Glass>
            )}
          </div>

          {/* Feed previews */}
          {feeds.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-white mb-4 flex items-center gap-2"><ImageIcon className="h-4 w-4 text-sky-400" /> Instagram Feed Posts</p>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {feeds.map(p => (
                  <div key={p.id} className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-zinc-300">{p.day_name} · {p.title}</p>
                      <Pill tone={p.status === 'approved' ? 'emerald' : p.status === 'rejected' ? 'red' : 'zinc'}>{p.status}</Pill>
                    </div>
                    <InstagramPostCard post={p} />
                    {productAnchor(p)}
                    {feedActions(p)}

                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Story previews */}
          {stories.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-white mb-4">Instagram Stories — previewed on-device</p>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                {stories.map(p => (
                  <div key={p.id} className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-zinc-300">{p.day_name}</p>
                      <Pill tone={p.status === 'approved' ? 'emerald' : p.status === 'rejected' ? 'red' : 'zinc'}>{p.status}</Pill>
                    </div>
                    <StoryPhoneFrame post={p} />
                    {p.story_layout && <p className="text-[11px] text-zinc-500 leading-snug line-clamp-2">Art direction: {p.story_layout}</p>}
                    <div className="flex flex-wrap gap-1.5">
                      <GButton variant="success" className="px-2 py-1 text-[11px]" onClick={() => setStatus(p, 'approved')}><Check className="h-3 w-3" /></GButton>
                      <GButton variant="danger" className="px-2 py-1 text-[11px]" onClick={() => setStatus(p, 'rejected')}><X className="h-3 w-3" /></GButton>
                      <GButton variant="ghost" className="px-2 py-1 text-[11px]" onClick={() => regen(p)} disabled={busy === p.id}>{busy === p.id ? <Spinner /> : <RefreshCw className="h-3 w-3" />}</GButton>
                      <GButton variant="ghost" className="px-2 py-1 text-[11px]" onClick={() => genImage(p)} disabled={busy === p.id}>{busy === p.id ? <Spinner /> : <ImageIcon className="h-3 w-3" />}</GButton>
                      <GButton variant="ghost" className="px-2 py-1 text-[11px] text-red-300 hover:text-red-200" onClick={() => delOne(p)} disabled={busy === p.id} title="Delete permanently">
                        {busy === p.id ? <Spinner /> : <Trash2 className="h-3 w-3" />}
                      </GButton>
                    </div>
                    {productAnchor(p)}

                  </div>
                ))}
              </div>
            </div>
          )}

          {posts.length > 0 && (
            <div className="flex justify-end">
              <GButton onClick={() => goto(3)}>Continue to Approve <ChevronRight className="h-4 w-4" /></GButton>
            </div>
          )}
        </div>
      )}

      {/* ═════════ STEP 4 — APPROVE ═════════ */}
      {step === 3 && (
        <div className="space-y-6">
          {week ? (
            <>
              <Glass className="p-6">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="rounded-2xl bg-emerald-500/15 p-3 text-emerald-300"><ClipboardCheck className="h-5 w-5" /></div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-white font-semibold">{week.theme}</p>
                      <Pill tone={weekType.tone}>{weekType.label}</Pill>
                    </div>
                    <p className="text-sm text-zinc-400">{approvedCount} approved · {rejectedCount} rejected · {posts.length - approvedCount - rejectedCount} pending · {withImages} with images</p>
                  </div>
                  <GButton variant="success" onClick={approveAllRemaining} disabled={posts.length === 0 || approvedCount === posts.length}>
                    <Check className="h-4 w-4" /> Approve all remaining ({posts.length - approvedCount})
                  </GButton>
                </div>
              </Glass>
              <div className="space-y-3">
                {dayGroups.map(g => (
                  <Glass key={g.dayIndex} className="p-4">
                    <p className="text-sm font-semibold text-white mb-3">{g.dayName}</p>
                    <div className="space-y-2">
                      {g.items.map(p => (
                        <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.03] p-3">
                          {p.post_type === 'feed' ? <ImageIcon className="h-4 w-4 text-sky-400 shrink-0" /> : <Instagram className="h-4 w-4 text-fuchsia-400 shrink-0" />}
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm text-zinc-200">{p.post_type === 'feed' ? (p.title || 'Feed post') : (p.story_text || 'Story')}</p>
                            {p.revision_notes && <p className="truncate text-[11px] text-red-300/80">Notes: {p.revision_notes}</p>}
                          </div>
                          <Pill tone={p.status === 'approved' ? 'emerald' : p.status === 'rejected' ? 'red' : 'zinc'}>{p.status}</Pill>
                          <div className="flex gap-1.5">
                            <GButton variant="success" className="px-2 py-1 text-[11px]" onClick={() => setStatus(p, 'approved')}><Check className="h-3 w-3" /></GButton>
                            <GButton variant="danger" className="px-2 py-1 text-[11px]" onClick={() => setStatus(p, 'rejected')}><X className="h-3 w-3" /></GButton>
                            <GButton variant="ghost" className="px-2 py-1 text-[11px]" onClick={() => { setEditing(p); setECaption(p.caption || ''); setEHashtags(p.hashtags || ''); setEPrompt(p.image_prompt || ''); }}><Pencil className="h-3 w-3" /></GButton>
                            <GButton variant="ghost" className="px-2 py-1 text-[11px]" onClick={() => regen(p)} disabled={busy === p.id}>{busy === p.id ? <Spinner /> : <RefreshCw className="h-3 w-3" />}</GButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Glass>
                ))}
              </div>
              <div className="flex justify-between">
                <GButton variant="ghost" onClick={() => goto(2)}><ChevronLeft className="h-4 w-4" /> Back to previews</GButton>
                <GButton onClick={() => goto(4)}>Continue to Publish <ChevronRight className="h-4 w-4" /></GButton>
              </div>
            </>
          ) : (
            <Glass className="p-10 text-center"><p className="text-sm text-zinc-400">No campaign selected — go back to Review & Edit and pick one from the library.</p></Glass>
          )}
        </div>
      )}

      {/* ═════════ STEP 5 — PUBLISH ═════════ */}
      {step === 4 && (
        <div className="space-y-6">
          {week ? (
            <Glass className="p-8">
              <div className="flex flex-col items-center text-center gap-3 mb-6">
                <div className="rounded-2xl bg-sky-500/15 p-4 text-sky-300"><Rocket className="h-7 w-7" /></div>
                <div>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <p className="text-xl font-semibold text-white">{week.theme}</p>
                    <Pill tone={weekType.tone}>{weekType.label} campaign</Pill>
                  </div>
                  <p className="mt-1 text-sm text-zinc-400 max-w-xl">{week.strategy}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-4 text-center"><p className="text-2xl font-semibold text-white">{feeds.length}</p><p className="text-xs text-zinc-500">Feed posts</p></div>
                <div className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-4 text-center"><p className="text-2xl font-semibold text-white">{stories.length}</p><p className="text-xs text-zinc-500">Stories</p></div>
                <div className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-4 text-center"><p className="text-2xl font-semibold text-emerald-300">{approvedCount}</p><p className="text-xs text-zinc-500">Approved</p></div>
                <div className="rounded-xl border border-white/[0.07] bg-white/[0.03] p-4 text-center"><p className="text-2xl font-semibold text-sky-300">{withImages}</p><p className="text-xs text-zinc-500">Images generated</p></div>
              </div>
              {approvedCount < posts.length && (
                <p className="mb-4 text-center text-xs text-amber-300">{posts.length - approvedCount} item{posts.length - approvedCount > 1 ? 's are' : ' is'} still awaiting approval — you can go back to the Approve step at any time.</p>
              )}
              <div className="flex flex-wrap justify-center gap-3">
                <GButton variant="ghost" onClick={() => goto(3)}><ChevronLeft className="h-4 w-4" /> Back to Approve</GButton>
                <GButton onClick={markReady} disabled={publishing || week.status === 'ready'}>
                  {publishing ? <Spinner /> : <Send className="h-4 w-4" />} {week.status === 'ready' ? 'Campaign marked ready' : 'Mark campaign ready to publish'}
                </GButton>
                <GButton variant="ghost" onClick={() => goto(0)}><Sparkles className="h-4 w-4" /> Start a new campaign</GButton>
              </div>
              {week.status === 'ready' && <p className="mt-4 text-center text-xs text-emerald-300">This campaign is locked in — export the approved content from the Social Media Hub's Ready Library.</p>}
            </Glass>
          ) : (
            <Glass className="p-10 text-center"><p className="text-sm text-zinc-400">No campaign selected — go back and pick one from the library.</p></Glass>
          )}
        </div>
      )}

      {/* Edit modal (available from Review and Approve steps) */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <Glass className="w-full max-w-xl p-6 bg-zinc-950/95">
            <div className="flex items-center justify-between mb-4">
              <p className="text-white font-semibold">Edit {editing.day_name} {editing.post_type}</p>
              <button onClick={() => setEditing(null)} className="text-zinc-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <Field label="Caption"><textarea className={inputCls + ' min-h-[130px]'} value={eCaption} onChange={e => setECaption(e.target.value)} /></Field>
              <Field label="Hashtags"><textarea className={inputCls} rows={2} value={eHashtags} onChange={e => setEHashtags(e.target.value)} /></Field>
              <Field label="Image prompt"><textarea className={inputCls} rows={3} value={ePrompt} onChange={e => setEPrompt(e.target.value)} /></Field>
              <div className="flex gap-2 justify-end">
                <GButton variant="ghost" onClick={() => setEditing(null)}>Cancel</GButton>
                <GButton onClick={saveEdit}><Check className="h-4 w-4" /> Save changes</GButton>
              </div>
            </div>
          </Glass>
        </div>
      )}
    </div>
  );
}
